import { useRef, useState } from "react";
import { Loader2, Sparkles, TriangleAlert, WandSparkles } from "lucide-react";
import { AiUnavailable, planFromText } from "../lib/api";
import { dictionaries, type Dictionary } from "../lib/i18n";
import type { TripForm, UiLang } from "../lib/types";

/**
 * Turen beskrevet med egne ord.
 *
 * Det den gjør, er å fylle ut skjemaet under. Ikke å lage planen — det gjør den
 * samme planleggeren som ellers, av det samme skjemaet. Fritekst er en annen
 * vei inn, ikke en annen motor, og det er grunnen til at en språkmodell som er
 * nede eller tom for kvote ikke tar med seg produktet i fallet.
 *
 * `understood` og `assumed` er ikke pynt. En modell som feiltolker, produserer
 * et helt gyldig skjema med feil innhold: «sju dager i september» blir til fire
 * dager i mars, planen ser riktig ut, og ingenting sier ifra. Å vise hva som
 * ble forstått og hva som ble antatt, er forskjellen på en funksjon som hjelper
 * og en som villeder.
 */

/** Feltene vi har et navn på i alle fem språk. */
type NamedField = keyof Dictionary["aiFieldName"];

interface Props {
  lang: UiLang;
  /** Kalles med det utfylte skjemaet. Planen lages av kalleren, som ellers. */
  onFilled: (form: TripForm) => void;
  /**
   * Uten eget kort og uten egen overskrift.
   *
   * Brukes når feltet ligger inne i «vet ikke hvor jeg skal»-panelet, som
   * allerede har begge deler. Et kort inni et kort ser ut som to funksjoner,
   * og de to er ett spørsmål med to måter å svare på.
   */
  embedded?: boolean;
}

export function TripFromText({ lang, onFilled, embedded = false }: Props) {
  const t = dictionaries[lang];
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [understood, setUnderstood] = useState<string | null>(null);
  const [assumed, setAssumed] = useState<NamedField[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!text.trim() || busy) return;

    setBusy(true);
    setError(null);
    setUnderstood(null);
    setAssumed([]);

    try {
      const result = await planFromText(text, lang);
      setUnderstood(result.understood);
      /*
       * Bare feltene vi har et navn for. Modellen kan i prinsippet føre opp
       * hva som helst, og en liste med «vehicle» og «startDate» på engelsk midt
       * i en fransk setning er verre enn en kortere liste.
       */
      setAssumed(
        result.assumed.filter((field): field is NamedField => field in t.aiFieldName),
      );
      onFilled(result.form);
    } catch (cause) {
      /*
       * En utilgjengelig modell er ikke en feil brukeren har gjort, og skal
       * ikke se ut som en. Beskjeden sier hva som fortsatt virker.
       */
      setError(
        cause instanceof AiUnavailable
          ? t.aiUnavailable
          : cause instanceof Error
            ? cause.message
            : t.aiUnavailable,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={embedded ? "from-text from-text--bare" : "from-text"}>
      {!embedded && (
        <h2 className="from-text-title">
          <WandSparkles size={16} />
          {t.aiTitle}
        </h2>
      )}
      <p className="from-text-intro">{t.aiIntro}</p>

      {/*
        Klikkbare, ikke bare lest. Poenget er ikke inspirasjon — det er å vise
        formen på et spørsmål modellen faktisk kan svare på, før den som
        skriver har bommet og lurer på hvorfor svaret ble rart. Et klikk
        fyller feltet og lar brukeren redigere videre; det sender ingenting,
        for et eksempel er ikke en bekreftelse på at akkurat den turen er
        ønsket.
      */}
      <div className="from-text-examples">
        <span className="from-text-examples-label">{t.aiExamplesLabel}:</span>
        {t.aiExamples.map((example) => (
          <button
            key={example}
            type="button"
            className="from-text-example"
            onClick={() => {
              setText(example);
              inputRef.current?.focus();
            }}
          >
            {example}
          </button>
        ))}
      </div>

      <form className="from-text-form" onSubmit={submit}>
        <textarea
          ref={inputRef}
          className="from-text-input"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t.aiPlaceholder}
          rows={3}
          maxLength={600}
          aria-label={t.aiTitle}
        />
        <button className="from-text-cta" type="submit" disabled={busy || !text.trim()}>
          {busy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
          {busy ? t.aiBusy : t.aiCta}
        </button>
      </form>

      {understood && (
        <p className="from-text-understood">
          <strong>{t.aiUnderstood}:</strong> {understood}
        </p>
      )}

      {assumed.length > 0 && (
        <p className="from-text-assumed">
          {t.aiAssumed}: {assumed.map((field) => t.aiFieldName[field]).join(", ")}.
        </p>
      )}

      {error && (
        <p className="from-text-error">
          <TriangleAlert size={14} />
          {error}
        </p>
      )}
    </section>
  );
}
